/**
 * Centralised TanStack Query keys for the store-settings feature.
 *
 * Keys are namespaced under `store-settings` so the whole feature can be
 * invalidated at once (e.g. on tenant switch) and individual resources stay
 * cache-isolated. Provider lists are nested under their owning domain.
 */

export const SETTINGS_ROOT_KEY = 'store-settings';

export const settingsKeys = {
  /** Root key — invalidate to drop every settings resource (e.g. tenant switch). */
  all: [SETTINGS_ROOT_KEY] as const,

  general: [SETTINGS_ROOT_KEY, 'general'] as const,

  delivery: [SETTINGS_ROOT_KEY, 'delivery'] as const,
  deliveryProviders: [SETTINGS_ROOT_KEY, 'delivery', 'providers'] as const,

  payments: [SETTINGS_ROOT_KEY, 'payments'] as const,
  paymentProviders: [SETTINGS_ROOT_KEY, 'payments', 'providers'] as const,

  checkout: [SETTINGS_ROOT_KEY, 'checkout'] as const,

  orders: [SETTINGS_ROOT_KEY, 'orders'] as const,
  ordersLifecycle: [SETTINGS_ROOT_KEY, 'orders', 'lifecycle'] as const,

  pricing: [SETTINGS_ROOT_KEY, 'pricing'] as const,

  catalog: [SETTINGS_ROOT_KEY, 'catalog'] as const,

  /** The unified sales-channel & touchpoint registry (adr-tenant-001, plan-tenant-001 Phase 5). */
  salesChannels: [SETTINGS_ROOT_KEY, 'sales-channels'] as const,

  customers: [SETTINGS_ROOT_KEY, 'customers'] as const,

  notifications: [SETTINGS_ROOT_KEY, 'notifications'] as const,
  notificationProviders: [SETTINGS_ROOT_KEY, 'notifications', 'providers'] as const,

  content: [SETTINGS_ROOT_KEY, 'content'] as const,

  engagement: [SETTINGS_ROOT_KEY, 'engagement'] as const,

  telegram: [SETTINGS_ROOT_KEY, 'telegram'] as const,
  telegramChannel: [SETTINGS_ROOT_KEY, 'telegram', 'channel'] as const,
  // `max` now also carries the transport/messaging fields formerly at `maxService` — WP-BFF.2 of
  // plan-conversation-001 merged `/api/v1/store/settings/max/service` into `/api/v1/store/settings/max`
  // server-side (adr-merchant-portal-015); `maxService` is retired, not just renamed.
  max: [SETTINGS_ROOT_KEY, 'max'] as const,

  // The channel-agnostic chatbot surface (plan-conversation-001 / plan-merchant-portal-019 WP2).
  // `chatbotGeneral` names the GENERAL (channel-agnostic) row of `/api/v1/store/settings/chatbot` —
  // read/written by `features/chatbot-settings`'s `ChatbotSettingsPage` (the interim bridge card
  // that used to own this key on `TelegramSettingsPage` was retired into that page in WP2).
  // `chatbotChannel`/`chatbotEffective` are the per-channel override and resolved-view resources
  // that page grew on top of it. Every chatbot key stays registered HERE (not duplicated in
  // `features/chatbot-settings`) — the established cross-feature-consumption pattern (see
  // `features/content`): a feature reads a shared resource's keys through this registry rather than
  // minting its own, so a tenant switch (which invalidates `settingsKeys.all`) evicts every one of
  // them in a single call.
  chatbotGeneral: [SETTINGS_ROOT_KEY, 'chatbot', 'general'] as const,
  /** `channel` is a plain string (`'TELEGRAM' | 'MAX'`) — kept untyped here so this shared registry
   * never imports a type from a feature it does not otherwise depend on (FSD direction). */
  chatbotChannel: (channel: string) => [SETTINGS_ROOT_KEY, 'chatbot', 'channel', channel] as const,
  /** Omit `channel` for the general resolution (`GET …/chatbot/effective` with no query param). */
  chatbotEffective: (channel?: string) =>
    [SETTINGS_ROOT_KEY, 'chatbot', 'effective', channel ?? 'GENERAL'] as const,
  chatbotAvailability: [SETTINGS_ROOT_KEY, 'chatbot', 'availability'] as const,
  chatbotTemplates: (locale: string) =>
    [SETTINGS_ROOT_KEY, 'chatbot', 'templates', locale] as const,
  chatbotTemplate: (key: string, locale: string, channel?: string) =>
    [SETTINGS_ROOT_KEY, 'chatbot', 'templates', 'item', key, locale, channel ?? 'GENERAL'] as const,
  /** *(contract delta 2026-09-23)* The catalogue list — `getChatbotTemplateCatalog(locale, channel?)`,
   * replacing the per-key `chatbotTemplates`/`chatbotTemplate` composition (`useChatbotTemplatesCatalog`
   * is deleted; templates doc §13.1). Kept as its own key (not nested under `chatbotTemplates`) so the
   * old per-key item cache and the new catalogue cache invalidate independently. */
  chatbotTemplateCatalog: (locale: string, channel?: string) =>
    [SETTINGS_ROOT_KEY, 'chatbot', 'templateCatalog', locale, channel ?? 'GENERAL'] as const,
  chatbotFaq: (locale: string) => [SETTINGS_ROOT_KEY, 'chatbot', 'faq', locale] as const,
  chatbotLanguages: [SETTINGS_ROOT_KEY, 'chatbot', 'languages'] as const,
  /** *(contract delta 2026-09-23)* `GET …/chatbot/dictionaries` — commands, intents, tools,
   * providers, enums (adr-conversation-013 D2). Identical for every tenant of a contour;
   * `staleTime: Infinity`, evicted only by the settings-root tenant-switch invalidation. */
  chatbotDictionaries: [SETTINGS_ROOT_KEY, 'chatbot', 'dictionaries'] as const,

  // ── Platform services added in docs/obzr-ecom-platform.md ──────────────────
  tenant: [SETTINGS_ROOT_KEY, 'tenant'] as const,
  store: [SETTINGS_ROOT_KEY, 'store'] as const,
  storeLocations: (params?: unknown) => [SETTINGS_ROOT_KEY, 'store', 'locations', params] as const,
  storeRegions: (params?: unknown) => [SETTINGS_ROOT_KEY, 'store', 'regions', params] as const,
  storeAssignments: [SETTINGS_ROOT_KEY, 'store', 'assignments'] as const,
  storeServiceSettings: [SETTINGS_ROOT_KEY, 'store', 'service-settings'] as const,
  /**
   * Live DaData-backed address lookup for `LocationDialog`'s address field — query text is
   * part of the key so each distinct search has its own cache entry. Falls under the
   * `[SETTINGS_ROOT_KEY, 'store']` prefix `useInvalidateStoreDomain` invalidates; harmless
   * (a stale suggestions entry just refetches next time it's enabled), not load-bearing.
   */
  addressSuggestions: (query: string) =>
    [SETTINGS_ROOT_KEY, 'store', 'address-suggestions', query] as const,
  address: [SETTINGS_ROOT_KEY, 'address'] as const,
  inventory: [SETTINGS_ROOT_KEY, 'inventory'] as const,
  media: [SETTINGS_ROOT_KEY, 'media'] as const,
  aiAgents: [SETTINGS_ROOT_KEY, 'ai-agents'] as const,
  basket: [SETTINGS_ROOT_KEY, 'basket'] as const,

  retailEvotor: [SETTINGS_ROOT_KEY, 'retail-evotor'] as const,
  // Params-free "root" keys are what mutations invalidate: TanStack Query's `invalidateQueries`
  // matches by *prefix*, so invalidating the root (not a params-appended key) catches every
  // paginated/filtered variant of the list in one call — mirrors `useInvalidateStoreDomain`'s
  // `[SETTINGS_ROOT_KEY, 'store']` prefix invalidation for `storeLocations(params)`.
  retailEvotorAccountsRoot: [SETTINGS_ROOT_KEY, 'retail-evotor', 'accounts'] as const,
  retailEvotorAccounts: (params?: unknown) =>
    [SETTINGS_ROOT_KEY, 'retail-evotor', 'accounts', params] as const,
  retailEvotorStoresRoot: [SETTINGS_ROOT_KEY, 'retail-evotor', 'stores'] as const,
  retailEvotorStores: (params?: unknown) =>
    [SETTINGS_ROOT_KEY, 'retail-evotor', 'stores', params] as const,
  retailEvotorDevicesRoot: [SETTINGS_ROOT_KEY, 'retail-evotor', 'devices'] as const,
  retailEvotorDevices: (params?: unknown) =>
    [SETTINGS_ROOT_KEY, 'retail-evotor', 'devices', params] as const,

  // The "Price list" select on the Stores tab (design spec §8.7) — a local copy of the SAME
  // `catalog.pricing.price-lists` BFF resource; namespaced under `retail-evotor` (NOT `pricing`,
  // which already names the unrelated `PricingSettingsPage` engine-mode resource) so there is no
  // key collision between the two completely different things this app calls "pricing settings".
  retailEvotorPriceLists: [SETTINGS_ROOT_KEY, 'retail-evotor', 'price-lists'] as const,
  retailEvotorPriceListBindings: (code: string) =>
    [SETTINGS_ROOT_KEY, 'retail-evotor', 'price-lists', code, 'bindings'] as const,

  erpPosiflora: [SETTINGS_ROOT_KEY, 'erp-posiflora'] as const,

  evotorIntegration: [SETTINGS_ROOT_KEY, 'evotor-integration'] as const,
} as const;
